import Close from "@mui/icons-material/Close";
import Delete from "@mui/icons-material/Delete";
import Save from "@mui/icons-material/Save";
import { Box, IconButton, Menu, TextField } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import { type ChangeEvent, type FC, type KeyboardEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useWeekViewContext } from "@/contexts/WeekView";
import { useHandleCensus } from "@/hooks/useHandleCensus";
import { useEditLocations } from "@/pages/MasterSchedule/v2/hooks";
import { INK_BLACK } from "@/theme/tokens";
import SystemDialog from "@/ui/Popups/Dialog";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { formatCensusReplacementNotice } from "@/utils/censusReplacement";
import { byDimensionsOf, censusDimensionKey } from "@/utils/censusResolution";
import { type ChangesSource, EmrType } from "@/utils/models";
import { getCensusSourceLabel, getCensusSourceName, getShiftCensus } from "@/utils/utilFunctions";
import { EditLocationCensus } from "./EditLocationCensus";

type EditCensusMenuProps = { dayDate: DateTime; handleMenuClose: () => void; menuAnchorEl: HTMLElement | null; source: ChangesSource };

const shiftNo = 0;

/**
 * Translate the editor's human-readable maps into canonical dimension keys for the wire.
 *
 * The composite map arrives keyed `"unit|floor"` from EditLocationCensus; splitting on the FIRST
 * separator matches how that component builds it. A unit or floor containing a pipe would be
 * ambiguous, so such an entry is dropped rather than written to a wrong key.
 */
const toByDimensions = (
	unitCensus: Record<string, number>,
	floorCensus: Record<string, number>,
	combinedCensus: Record<string, number>,
): Record<string, number> => {
	const byDimensions: Record<string, number> = {};
	for (const [unit, count] of Object.entries(unitCensus)) byDimensions[censusDimensionKey(unit, null, null)] = count;
	for (const [floor, count] of Object.entries(floorCensus)) byDimensions[censusDimensionKey(null, floor, null)] = count;
	for (const [composite, count] of Object.entries(combinedCensus)) {
		const parts = composite.split("|");
		if (parts.length !== 2) continue;
		byDimensions[censusDimensionKey(parts[0], parts[1], null)] = count;
	}
	return byDimensions;
};

export const EditCensusMenu: FC<EditCensusMenuProps> = ({ dayDate, menuAnchorEl, handleMenuClose, source }) => {
	const { hospitalData, chosenDepartment, isHospitalView, isHospitalDemo, departmentEmrType } = useHospitalData();
	const { weekOfDate } = useWeekViewContext();
	const { canEditCensusByLocation } = useEditLocations();
	// True only when PCC is the active EMR (a disabled EMR resolves to undefined → manual census).
	const isPcc = departmentEmrType === EmrType.PCC;
	const { t } = useTranslation();

	// Found by `dayDate` so spillover cells (a visible day from the next data-week when the user picked a
	// non-default week-start) write to the correct data-week.
	const dayWeek = weekOfDate(dayDate);
	const week = dayWeek?.week;
	// -1 is "not in the weeks", as the position lookups say it; the menu then shows nothing (see below).
	const dayNo = dayWeek === undefined ? -1 : dayWeek.dayIndex;
	const { track } = useAnalytics();
	const { updateCensusOverride, deleteCensusOverride, acceptCensusReplacement, isLoading } = useHandleCensus();
	const [censusValue, setCensusValue] = useState<number>(-1);
	const [noteValue, setNoteValue] = useState<string>("");
	const [unitCensus, setUnitCensus] = useState<Record<string, number>>({});
	const [floorCensus, setFloorCensus] = useState<Record<string, number>>({});
	const [combinedCensus, setCombinedCensus] = useState<Record<string, number>>({});
	const [isCapacityWarningOpen, setIsCapacityWarningOpen] = useState<boolean>(false);
	const dayObj = week?.weeklyShifts[shiftNo]?.days[dayNo];
	const isPending = isLoading.updateCensus || isLoading.deleteCensus || isLoading.acceptCensus;

	useEffect(() => {
		if (menuAnchorEl) {
			track("edit_census_menu_opened", {
				day_no: dayNo,
				shift_no: shiftNo,
				date: dayObj?.date?.toFormat("yyyy-MM-dd"),
				shift_type: week?.weeklyShifts[shiftNo]?.type,
			});
		}
	}, [menuAnchorEl, dayNo, dayObj, week, track]);

	// Gated on `menuAnchorEl` because 7 instances mount per week (one per day);
	// without the gate, every week-change fires 5 setStates × 7 closed menus.
	useEffect(() => {
		if (!menuAnchorEl || !week || dayNo < 0) return;

		const dayObj = week.weeklyShifts[shiftNo]?.days[dayNo];
		const override = dayObj?.census_override;
		const pccCensus = getShiftCensus(week, shiftNo, dayNo);

		setCensusValue(override ? override.override_value : pccCensus);
		setNoteValue(override?.note || "");

		if (dayObj?.census_grouping) {
			const validUnits = hospitalData.units || [];
			const validFloors = hospitalData.floors || [];

			// The editor keeps its own human-readable shape (unit name, floor name, "unit|floor") because
			// that is what the input grid renders. Canonical dimension keys are translated in here and
			// back out on save, so EditLocationCensus never has to know about the wire format.
			const byDimensions = byDimensionsOf(dayObj.census_grouping);
			const readAt = (unit: string | null, floor: string | null): number => byDimensions[censusDimensionKey(unit, floor, null)] ?? 0;

			setUnitCensus(Object.fromEntries(validUnits.map((u) => [u, readAt(u, null)])));
			setFloorCensus(Object.fromEntries(validFloors.map((f) => [f, readAt(null, f)])));
			setCombinedCensus(
				Object.fromEntries(validUnits.flatMap((u) => validFloors.map((f) => [`${u}|${f}`, readAt(u, f)] as const)).filter(([, count]) => count > 0)),
			);
		} else {
			setUnitCensus({});
			setFloorCensus({});
			setCombinedCensus({});
		}
	}, [menuAnchorEl, dayNo, week, hospitalData.units, hospitalData.floors]);

	const menuClosedInternal = useCallback((): void => {
		track("edit_census_menu_closed", { day_no: dayNo, shift_no: shiftNo, date: dayDate.toFormat("yyyy-MM-dd") });
		handleMenuClose();
	}, [handleMenuClose, track, dayNo, dayDate]);

	const persistCensus = useCallback(async (): Promise<void> => {
		// The menu renders nothing without its day (see below), so nothing can submit then.
		if (!week || !dayObj) return;
		track("edit_census_menu_submitted", {
			action: "update census",
			date: dayObj?.date?.toFormat("yyyy-MM-dd"),
			date_diff: dayObj?.date.diff(DateTime.now(), "days").days,
			shift_type: week.weeklyShifts[shiftNo].type,
			census: censusValue,
			current_override_census: week.weeklyShifts[shiftNo]?.days[dayNo]?.census_override?.override_value,
			is_pcc: !isPcc,
			pcc_census: getShiftCensus(week, shiftNo, dayNo),
		});

		await updateCensusOverride({
			date: dayObj.date.toFormat("yyyy-MM-dd"),
			shiftType: week.weeklyShifts[shiftNo].type,
			overrideValue: censusValue,
			note: noteValue,
			source,
			locationCensus: { by_dimensions: toByDimensions(unitCensus, floorCensus, combinedCensus) },
		});

		menuClosedInternal();
	}, [censusValue, track, dayObj, week, dayNo, isPcc, updateCensusOverride, noteValue, source, menuClosedInternal, unitCensus, floorCensus, combinedCensus]);

	const handleCensusSubmit = useCallback(async (): Promise<void> => {
		track("save_census_clicked", {
			day_no: dayNo,
			shift_no: shiftNo,
			census_value: censusValue,
			max_beds: chosenDepartment.total_beds,
			date: dayObj?.date?.toFormat("yyyy-MM-dd"),
		});

		if (censusValue < 0) return;

		if (chosenDepartment.total_beds > 0 && censusValue > chosenDepartment.total_beds) {
			setIsCapacityWarningOpen(true);
			return;
		}

		await persistCensus();
	}, [track, dayNo, censusValue, dayObj, chosenDepartment.total_beds, persistCensus]);

	// Enter submits. Setting a week of census is seven of these popovers, and Escape already closed one
	// while Enter did nothing -- so the keyboard could abandon the form but not complete it, and every
	// day cost a mouse trip to Save. The note field is deliberately excluded: it's multiline, where
	// Enter means newline.
	const handleCensusKeyDown = useCallback(
		(event: KeyboardEvent<HTMLInputElement>): void => {
			if (event.key !== "Enter" || event.shiftKey || isPending) return;
			event.preventDefault();
			void handleCensusSubmit();
		},
		[handleCensusSubmit, isPending],
	);

	const handleCapacityWarningConfirm = useCallback(async (): Promise<void> => {
		setIsCapacityWarningOpen(false);
		await persistCensus();
	}, [persistCensus]);

	const handleAcceptReplacement = useCallback(async (): Promise<void> => {
		if (!dayObj?.date) return;
		track("accept_census_replacement_clicked", { day_no: dayNo, shift_no: shiftNo, date: dayObj.date.toFormat("yyyy-MM-dd") });
		const date = dayObj.date.toFormat("yyyy-MM-dd");
		// Close first. The dismiss only clears the warning; waiting on it, then reloading the
		// schedule, left this menu up and then moved the grid to another week.
		menuClosedInternal();
		await acceptCensusReplacement({ date });
	}, [acceptCensusReplacement, dayNo, dayObj, menuClosedInternal, track]);

	const handleDeleteOverride = useCallback(async (): Promise<void> => {
		// The menu renders nothing without its day (see below), so nothing can delete then.
		if (!week || !dayObj) return;
		track("delete_census_override_clicked", { day_no: dayNo, shift_no: shiftNo, date: dayObj?.date?.toFormat("yyyy-MM-dd") });
		await deleteCensusOverride({
			date: dayObj.date.toFormat("yyyy-MM-dd"),
			shiftType: week.weeklyShifts[shiftNo].type,
			source,
		});
		menuClosedInternal();
	}, [track, dayObj, week, deleteCensusOverride, source, dayNo, menuClosedInternal]);

	const editNote = (e: ChangeEvent<HTMLInputElement>): void => {
		const newNote = e.target.value;

		if (newNote.length <= 120) {
			setNoteValue(newNote);
			track("note_value_changed", {
				day_no: dayNo,
				shift_no: shiftNo,
				previous_value: noteValue,
				new_value: newNote,
				date: dayObj?.date?.toFormat("yyyy-MM-dd"),
			});
		}
	};

	const isHospitalDemoView = isHospitalView || isHospitalDemo;
	const emrName = getCensusSourceName(chosenDepartment, isHospitalDemoView);
	const censusSourceLabel = getCensusSourceLabel(chosenDepartment, isHospitalDemoView);

	// `dayDate` can fall outside the week's data — an out-of-range URL date, a week still loading, or the
	// brief window where a week's `weeklyShifts[0].days` has not hydrated. There is no data-week to read or
	// write then, so bail: the `getShiftCensus` call below runs during render (JSX props evaluate eagerly,
	// even with the Menu closed). Placed after every hook so hook order stays stable across the
	// resolvable/unresolvable transition.
	if (!week || dayNo < 0 || !dayObj) return null;

	return (
		<>
			<SystemDialog
				dataTestId="census-capacity-warning"
				open={isCapacityWarningOpen}
				onClose={() => setIsCapacityWarningOpen(false)}
				title={t("edit.census.exceeds.capacity.title")}
				body={t("edit.census.exceeds.capacity.body", { newCensus: censusValue, totalBeds: chosenDepartment.total_beds })}
				onConfirm={handleCapacityWarningConfirm}
				confirm={t("edit.census.exceeds.capacity.save.anyway")}
			/>
			<Menu
				anchorEl={menuAnchorEl}
				open={!!menuAnchorEl}
				onClose={menuClosedInternal}
				onClick={(e) => e.stopPropagation()}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				transformOrigin={{ vertical: "top", horizontal: "center" }}
				sx={{ "& .MuiPaper-root": { boxShadow: `0px 1px 3px ${alpha(INK_BLACK, 0.1)}` } }}
			>
				<IconButton onClick={menuClosedInternal} sx={{ position: "absolute", top: 0, right: 0 }}>
					<Close />
				</IconButton>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						gap: 2,
						p: 1,
						pt: 3,
						minWidth: "300px",
						width: isPcc ? "20vw" : "15vw",
						maxWidth: "400px",
					}}
				>
					<Box sx={{ display: "flex", flexDirection: "row", gap: 2, width: "100%" }}>
						<TextField
							autoFocus
							size="small"
							label={isPcc ? "Manual Census" : "Census"}
							type="number"
							fullWidth
							value={censusValue}
							onChange={(e) => setCensusValue(+e.target.value)}
							onKeyDown={handleCensusKeyDown}
							slotProps={{ htmlInput: { min: 0, "data-testid": "census-value-input" } }}
							sx={{ width: "100%" }}
						/>
						<TextField
							size="small"
							label={censusSourceLabel}
							fullWidth
							value={getShiftCensus(week, shiftNo, dayNo)}
							slotProps={{ htmlInput: { readOnly: true } }}
							disabled
							sx={{ width: "100%", display: !isPcc ? "none" : "block" }}
						/>
					</Box>
					<TextField
						size="small"
						label="Note"
						fullWidth
						value={noteValue}
						onChange={editNote}
						multiline
						maxRows={2}
						slotProps={{ htmlInput: { maxLength: 120 } }}
						helperText={`${noteValue.length}/120 characters`}
						sx={{ width: "100%", display: !isPcc ? "none" : "block" }}
					/>

					{dayObj.census_replacement && (
						<Box
							data-testid="census-replacement-notice"
							sx={{
								width: "100%",
								display: "flex",
								alignItems: "center",
								gap: 1.5,
								px: 1.25,
								py: 1,
								borderRadius: 1,
								color: "warning.dark",
								fontSize: 13,
								lineHeight: 1.4,
								backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.12),
							}}
						>
							<Box sx={{ flex: 1 }}>{formatCensusReplacementNotice(t, emrName, dayObj.census_replacement)}</Box>
							<SystemButton
								data-testid="census-replacement-accept"
								onClick={handleAcceptReplacement}
								buttonVariant="secondary"
								size="small"
								loading={isLoading.acceptCensus}
								disabled={isPending}
								sx={{ flexShrink: 0 }}
							>
								{t("census.replacement.accept")}
							</SystemButton>
						</Box>
					)}

					{canEditCensusByLocation && (
						<EditLocationCensus
							unitCensus={unitCensus}
							floorCensus={floorCensus}
							combinedCensus={combinedCensus}
							censusValue={censusValue}
							onUnitCensusChange={setUnitCensus}
							onFloorCensusChange={setFloorCensus}
							onCombinedCensusChange={setCombinedCensus}
						/>
					)}

					<Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
						<SystemButton
							data-testid="census-save-button"
							onClick={handleCensusSubmit}
							size="small"
							leadingIcon={<Save fontSize="small" />}
							loading={isLoading.updateCensus}
							disabled={isPending}
						>
							Save
						</SystemButton>
						{!!dayObj?.census_override && (
							<SystemButton
								onClick={handleDeleteOverride}
								buttonVariant="secondary"
								size="small"
								leadingIcon={<Delete fontSize="small" />}
								loading={isLoading.deleteCensus}
								disabled={isPending}
							>
								Restore to {emrName}
							</SystemButton>
						)}
					</Box>
				</Box>
			</Menu>
		</>
	);
};
